/**
 * Graficos do dashboard (spec 053, 3.2): linha (uma ou mais series no tempo), barras (por categoria) e a comparacao
 * com o periodo anterior. Sem biblioteca: SVG e CSS com os tokens, dia e noite. Nenhum sabe a marca nem o que o numero
 * significa: o app manda os valores prontos e o jeito de escrever cada um. O leitor de tela le a tabela que fica por
 * baixo de cada grafico (o desenho e so pra quem ve).
 */
import { useId, useState, type PointerEvent, type ReactNode } from "react";
import { IconeDesceu, IconeSeta, IconeSubiu } from "../icones";
import { LinkInterno } from "../navegacao";

export type TomDoGrafico = "acento" | "info" | "sucesso" | "alerta" | "erro";
/** `null` e ponto sem dado (a taxa de um mes sem ninguem finalizado): a linha pula, nao desce a zero. */
export type SerieDoGrafico = { nome: string; valores: (number | null)[]; tom?: TomDoGrafico };

const numero = (v: number) => v.toLocaleString("pt-BR");

/**
 * O topo do eixo: o maior valor arredondado pra cima numa conta redonda (1, 2, 4, 6, 8 e 10 vezes uma potencia de
 * dez), pra o meio do eixo tambem sair redondo. Contagem pequena nao ganha meio quebrado (topo 2, meio 1).
 */
export function topoDoEixo(maior: number): number {
  if (!(maior > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(maior));
  const f = maior / p;
  const topo = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 4 ? 4 : f <= 6 ? 6 : f <= 8 ? 8 : 10) * p;
  return topo === 1 && maior >= 1 ? 2 : topo;
}

/** No maximo cinco rotulos no eixo de baixo (cabem no celular), sempre com o primeiro e o ultimo. */
function marcasDoEixo(n: number): number[] {
  if (n <= 5) return Array.from({ length: n }, (_, i) => i);
  const passo = (n - 1) / 4;
  return [...new Set(Array.from({ length: 5 }, (_, i) => Math.round(i * passo)))];
}

/**
 * Linha no tempo: faturamento por dia, entradas nas etapas, taxa por mes. `rotulos` sao os pontos do eixo de baixo ja
 * escritos ("16/10", "out/26"); `formatar` escreve cada valor (na dica e na tabela), `formatarEixo` o eixo da esquerda
 * (mais curto: "R$ 12 mil"). Passar o dedo ou o mouse mostra os valores do ponto.
 */
export function GraficoDeLinha({ rotulo, rotulos, series, formatar = numero, formatarEixo = formatar, topo: topoFixo, altura = 200, semValor = "Sem dado", vazio = "Nada no período." }: { rotulo: string; rotulos: string[]; series: SerieDoGrafico[]; formatar?: (v: number) => string; formatarEixo?: (v: number) => string; /** Topo fixo do eixo (100 na taxa). */ topo?: number; altura?: number; semValor?: string; vazio?: ReactNode }) {
  const id = useId();
  const [foco, setFoco] = useState<number | null>(null);
  const n = rotulos.length;
  const valores = series.flatMap((s) => s.valores.filter((v): v is number => v != null));
  const maior = Math.max(0, ...valores);
  const topo = topoFixo ?? topoDoEixo(maior);
  const x = (i: number) => (n <= 1 ? 50 : (i / (n - 1)) * 100);
  const y = (v: number) => 100 - Math.min(100, Math.max(0, (v / topo) * 100));
  // Cada trecho sem buraco vira um "M ... L ...": o ponto sem dado separa a linha em dois.
  const caminho = (vs: (number | null)[]) => vs.map((v, i) => (v == null ? "" : `${i && vs[i - 1] != null ? "L" : "M"}${x(i)},${y(v)}`)).join(" ");
  const escrever = (v: number | null | undefined) => (v == null ? semValor : formatar(v));
  const mover = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setFoco(n <= 1 ? 0 : Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left) / r.width) * (n - 1)))));
  };
  // Sem movimento nenhum, o eixo so diz o zero (topo e meio "R$ 0" nao informam nada).
  const semMovimento = maior === 0 && topoFixo == null;
  const pontosVisiveis = foco != null ? [foco] : n === 1 ? [0] : [];
  const lado = foco == null ? "" : x(foco) < 20 ? "inicio" : x(foco) > 80 ? "fim" : "meio";
  return (
    <figure className="mtc-curva" aria-labelledby={`${id}-rotulo`}>
      <figcaption id={`${id}-rotulo`} className="mtc-so-leitor">{rotulo}</figcaption>
      {series.length > 1 && (
        <ul className="mtc-curva__legenda" aria-hidden>
          {series.map((s) => <li key={s.nome} data-tom={s.tom ?? "acento"}><span className="mtc-curva__cor" />{s.nome}</li>)}
        </ul>
      )}
      <div className="mtc-curva__corpo" aria-hidden>
        <div className="mtc-curva__eixo-y" style={{ height: altura }}>
          <span>{semMovimento ? "" : formatarEixo(topo)}</span>
          <span>{semMovimento ? "" : formatarEixo(topo / 2)}</span>
          <span>{formatarEixo(0)}</span>
        </div>
        <div className="mtc-curva__area" style={{ height: altura }} onPointerMove={mover} onPointerDown={mover} onPointerLeave={() => setFoco(null)}>
          <svg className="mtc-curva__svg" viewBox="0 0 100 100" preserveAspectRatio="none">
            {[0, 50, 100].map((g) => <line key={g} className="mtc-curva__grade" x1="0" x2="100" y1={g} y2={g} vectorEffect="non-scaling-stroke" />)}
            {n > 1 && series[0] && series[0].valores.every((v) => v != null) && <path className="mtc-curva__preenchimento" data-tom={series[0].tom ?? "acento"} d={`${caminho(series[0].valores)} L100,100 L0,100 Z`} />}
            {n > 1 && series.map((s) => <path key={s.nome} className="mtc-curva__linha" data-tom={s.tom ?? "acento"} d={caminho(s.valores)} vectorEffect="non-scaling-stroke" />)}
          </svg>
          {foco != null && <span className="mtc-curva__guia" style={{ left: `${x(foco)}%` }} />}
          {pontosVisiveis.flatMap((i) => series.map((s) => (s.valores[i] == null ? null : <span key={`${s.nome}-${i}`} className="mtc-curva__ponto" data-tom={s.tom ?? "acento"} style={{ left: `${x(i)}%`, top: `${y(s.valores[i]!)}%` }} />)))}
          {/* Ponto sem vizinho com dado nao vira linha: aparece como ponto (o unico mes com alguem finalizado). */}
          {foco == null && n > 1 && series.flatMap((s) => s.valores.map((v, i) => (v != null && s.valores[i - 1] == null && s.valores[i + 1] == null ? <span key={`${s.nome}-solto-${i}`} className="mtc-curva__ponto" data-tom={s.tom ?? "acento"} style={{ left: `${x(i)}%`, top: `${y(v)}%` }} /> : null)))}
          {foco != null && (
            <div className="mtc-curva__dica" data-lado={lado} style={{ left: `${x(foco)}%` }}>
              <strong>{rotulos[foco]}</strong>
              {series.map((s) => <span key={s.nome} data-tom={s.tom ?? "acento"}>{series.length > 1 && <><span className="mtc-curva__cor" />{s.nome}: </>}{escrever(s.valores[foco])}</span>)}
            </div>
          )}
          {(valores.length === 0 || semMovimento) && foco == null && <span className="mtc-curva__vazio">{vazio}</span>}
        </div>
        <div className="mtc-curva__eixo-x">
          {marcasDoEixo(n).map((i) => <span key={i} data-ponta={n > 1 && i === 0 ? "inicio" : n > 1 && i === n - 1 ? "fim" : undefined} style={{ left: `${x(i)}%` }}>{rotulos[i]}</span>)}
        </div>
      </div>
      {/* A tabela fica numa caixa escondida: tabela nao respeita a largura de 1 px e, com quatro colunas, empurrava a pagina do celular pro lado. */}
      <div className="mtc-so-leitor">
        <table>
          <caption>{rotulo}</caption>
          <thead><tr><th scope="col">Quando</th>{series.map((s) => <th key={s.nome} scope="col">{s.nome}</th>)}</tr></thead>
          <tbody>{rotulos.map((r, i) => <tr key={`${r}-${i}`}><th scope="row">{r}</th>{series.map((s) => <td key={s.nome}>{escrever(s.valores[i])}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </figure>
  );
}

export type BarraDoGrafico = { chave?: string; rotulo: ReactNode; valor: number; detalhe?: ReactNode };

/**
 * Barras deitadas, uma por categoria (planos ativos por plano, pedidos por tipo). O rotulo e o numero ficam em cima da
 * barra, em texto: cabe nome longo de plano no celular e o leitor de tela le a lista como ela e.
 */
export function GraficoDeBarras({ rotulo, itens, formatar = numero, tom = "acento", vazio = "Nada no período.", limite }: { rotulo: string; itens: BarraDoGrafico[]; formatar?: (v: number) => string; tom?: TomDoGrafico; vazio?: ReactNode; /** Mostra so as primeiras; o resto abre no "Mostrar todos". */ limite?: number }) {
  const [todos, setTodos] = useState(false);
  const maior = Math.max(0, ...itens.map((i) => i.valor));
  const sobra = limite && itens.length > limite ? itens.length - limite : 0;
  const visiveis = sobra && !todos ? itens.slice(0, limite) : itens;
  return (
    <figure className="mtc-barras" aria-label={rotulo} data-tom={tom}>
      {itens.length === 0 ? (
        <p className="mtc-barras__vazio">{vazio}</p>
      ) : (
        <ul className="mtc-barras__lista">
          {visiveis.map((i, k) => (
            <li key={i.chave ?? k} className="mtc-barras__item">
              <span className="mtc-barras__rotulo">{i.rotulo}</span>
              <span className="mtc-barras__valor">{formatar(i.valor)}{i.detalhe && <span className="mtc-barras__detalhe">{i.detalhe}</span>}</span>
              <span className="mtc-barras__trilho" aria-hidden><span className="mtc-barras__barra" style={{ inlineSize: `${maior ? (i.valor / maior) * 100 : 0}%` }} /></span>
            </li>
          ))}
        </ul>
      )}
      {sobra > 0 && <button type="button" className="mtc-link mtc-barras__mais" aria-expanded={todos} onClick={() => setTodos(!todos)}>{todos ? "Mostrar menos" : `Mostrar todos (${itens.length})`}</button>}
    </figure>
  );
}

/**
 * A seta do numero contra o periodo anterior do mesmo tamanho (3.1): "12% acima do período anterior". `pontos`: a
 * diferenca de duas taxas (0 a 1), em pontos percentuais. `menosEMelhor`: subir pinta de erro (vencidas, atraso).
 * `curta`: so a seta e o quanto ("12%"), pra faixa de metricas; a frase inteira fica pro leitor de tela e no `title`.
 */
export function Comparacao({ atual, anterior, modo = "percentual", menosEMelhor, curta }: { atual: number | null | undefined; anterior: number | null | undefined; modo?: "percentual" | "pontos"; menosEMelhor?: boolean; curta?: boolean }) {
  const escrever = (classe: string, longo: string, curto: string, icone?: ReactNode) => (
    <span className={`mtc-comparacao${classe}`} title={curta ? longo : undefined}>
      {icone}
      {curta ? <><span aria-hidden>{curto}</span><span className="mtc-so-leitor">{longo}</span></> : longo}
    </span>
  );
  if (atual == null || anterior == null) return escrever("", "Sem comparação com o período anterior", "Sem base");
  const dif = atual - anterior;
  if (Math.abs(dif) < 1e-9) return escrever("", "Igual ao período anterior", "Igual");
  const sobe = dif > 0;
  const tom = ` mtc-comparacao--${sobe !== Boolean(menosEMelhor) ? "bom" : "ruim"}`;
  if (modo === "percentual" && anterior === 0) return escrever(tom, "Nada no período anterior", "Antes: 0", <IconeSubiu tamanho={16} />);
  const quanto = modo === "pontos" ? Math.round(Math.abs(dif) * 100) : Math.round((Math.abs(dif) / Math.abs(anterior)) * 100);
  const texto = modo === "pontos" ? (quanto ? `${quanto} ${quanto === 1 ? "ponto" : "pontos"}` : "menos de 1 ponto") : quanto ? `${numero(quanto)}%` : "menos de 1%";
  const curto = modo === "pontos" ? (quanto ? `${quanto} pts` : "<1 pt") : quanto ? `${numero(quanto)}%` : "<1%";
  return escrever(tom, `${texto} ${sobe ? "acima" : "abaixo"} do período anterior`, curto, sobe ? <IconeSubiu tamanho={16} /> : <IconeDesceu tamanho={16} />);
}

/**
 * Faixa de metricas do dashboard (Vitor, 17/10: "faça eles mais compactos ou mais interativos"): varios numeros numa
 * caixa so, com linha fina entre eles, em vez de um cartao grande pra cada. Com `abas`, as metricas escolhem o painel
 * de baixo (o grafico ou o detalhe do numero escolhido).
 */
export function Metricas({ rotulo, abas, children }: { rotulo: string; abas?: boolean; children: ReactNode }) {
  return <div className="mtc-metricas" role={abas ? "tablist" : "group"} aria-label={rotulo}>{children}</div>;
}

/**
 * Um numero da faixa: rotulo, valor, uma linha de apoio e a comparacao curta. E uma de tres coisas: so leitura; um link
 * (`href`, leva pra lista que explica o numero); ou uma aba (`aoEscolher`, com `selecionada`). `tom` pinta a metrica
 * que pede atencao (vencidas). `data-metrica` e o nome estavel pro e2e.
 */
export function Metrica({ rotulo, valor, apoio, comparacao, tom = "neutro", href, selecionada, aoEscolher, controla }: { rotulo: string; valor: ReactNode; apoio?: ReactNode; comparacao?: ReactNode; tom?: "neutro" | "alerta" | "erro"; href?: string; selecionada?: boolean; aoEscolher?: () => void; /** O id do painel que a aba mostra. */ controla?: string }) {
  const conteudo = (
    <>
      <span className="mtc-metrica__rotulo">{rotulo}{href && <IconeSeta tamanho={14} />}</span>
      <strong className="mtc-metrica__valor">{valor}</strong>
      {apoio && <span className="mtc-metrica__apoio">{apoio}</span>}
      {comparacao && <span className="mtc-metrica__comparacao">{comparacao}</span>}
    </>
  );
  const classe = `mtc-metrica mtc-metrica--${tom}`;
  if (href) return <LinkInterno href={href} className={`${classe} mtc-metrica--link`} data-metrica={rotulo}>{conteudo}</LinkInterno>;
  if (aoEscolher) return <button type="button" role="tab" aria-selected={Boolean(selecionada)} aria-controls={controla} className={classe} onClick={aoEscolher} data-metrica={rotulo}>{conteudo}</button>;
  return <div className={classe} data-metrica={rotulo}>{conteudo}</div>;
}
